import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { getToken, getTokensUpdatedAt } from '../services/tokenStorage';
import { getSocketUrl } from '../config/socket';

const SocketContext = createContext(null);

function isAdminUser(user) {
  if (!user) return false;
  return user.isAdmin === true || user.role === 'admin';
}

function isOperatorUser(user) {
  if (!user) return false;
  return user.isOperator === true || user.role === 'operator';
}

export function SocketProvider({ children }) {
  const { user } = useAuth();
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectError, setConnectError] = useState(null);
  const [tokenVersion, setTokenVersion] = useState(() => getTokensUpdatedAt());
  const socketRef = useRef(null);
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    const onTokensUpdated = () => setTokenVersion(getTokensUpdatedAt());
    window.addEventListener('smartgaz:tokens-updated', onTokensUpdated);
    return () => window.removeEventListener('smartgaz:tokens-updated', onTokensUpdated);
  }, []);

  useEffect(() => {
    const token = getToken() || user?.token;
    if (!token) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setSocket(null);
      setIsConnected(false);
      setConnectError(null);
      return undefined;
    }

    const newSocket = io(getSocketUrl(), {
      auth: (cb) => {
        const fresh = getToken() || userRef.current?.token;
        cb({ token: fresh });
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 15,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    socketRef.current = newSocket;

    const onConnect = () => {
      setIsConnected(true);
      setConnectError(null);
      const u = userRef.current;
      if (isAdminUser(u) || isOperatorUser(u)) {
        newSocket.emit('join_admin_room');
      }
    };

    newSocket.on('connect', onConnect);
    newSocket.on('disconnect', () => setIsConnected(false));
    newSocket.on('connect_error', (err) => {
      const msg = err?.message || String(err);
      console.error('WebSocket connect_error:', msg);
      setConnectError(msg);
      setIsConnected(false);
    });

    newSocket.on('profile_updated', (profile) => {
      window.dispatchEvent(new CustomEvent('smartgaz:profile-updated', { detail: profile }));
    });

    setSocket(newSocket);

    return () => {
      newSocket.off('connect', onConnect);
      newSocket.disconnect();
      if (socketRef.current === newSocket) socketRef.current = null;
    };
  }, [user?.id, user?.token, tokenVersion]);

  const joinRoute = useCallback((routeId) => {
    if (socketRef.current?.connected && routeId) {
      socketRef.current.emit('join_route', { routeId });
    }
  }, []);

  const leaveRoute = useCallback((routeId) => {
    if (socketRef.current?.connected && routeId) {
      socketRef.current.emit('leave_route', { routeId });
    }
  }, []);

  return (
    <SocketContext.Provider
      value={{ socket, isConnected, connectError, joinRoute, leaveRoute }}
    >
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}
